import { ROUTES } from '@sportninja/common/constants/app'

/**
 * SN-8340 — where an in-app Team-Managed Budget money notice takes the player.
 *
 * Two notices carry a payment link: the allocation notice ("your team payment schedule is
 * ready") and its reminder twin. Neither was navigable — `notificationTypes` had no entry
 * for either, so NotificationItem's switch fell through to '' and clicking did nothing.
 *
 * SN-8478 — both now link the share's Billing Schedule in My Registrations (every payment,
 * and Pay Full Balance) instead of one installment's checkout, as a RELATIVE path. Rows
 * stored before that still hold the single-payment `/pay` link — relative from the
 * allocation notice, ABSOLUTE from the reminder (built from the app URL) — and must keep
 * working, so the reader accepts every shape: Billing Schedule first, then `/pay`, then
 * My Registrations.
 */

/** "My Registrations" — the player's own list, and where every payable invoice is reachable. */
export const BALANCE_NOTIFICATION_FALLBACK: string = ROUTES.REGISTRATIONS.ROOT

const PAY_PATH = '/pay'

/**
 * The stored link, narrowed to THIS app's pay route — or null.
 *
 * Deliberately narrow. `history.push` accepts whatever it is given, so an absolute URL
 * taken from stored data is an open redirect waiting to happen: only the path and query
 * of a URL whose path is exactly `/pay` survive, and the host is discarded rather than
 * trusted. Anything else — another path, another scheme, junk — returns null and the
 * caller falls back.
 */
export const toPayRoute = (raw: unknown): string | null => {
  if (typeof raw !== 'string' || raw.length === 0) {
    return null
  }

  if (raw === PAY_PATH || raw.startsWith(`${PAY_PATH}?`)) {
    return raw
  }

  try {
    const parsed = new URL(raw)
    if (parsed.pathname === PAY_PATH) {
      return `${parsed.pathname}${parsed.search}`
    }
  } catch {
    // Not an absolute URL — nothing to normalise.
  }

  return null
}

/**
 * SN-8478 — the stored link narrowed to the share's Billing Schedule — or null.
 *
 * Matched on the RAW string against one anchored pattern, never normalised through `URL`
 * first (that would resolve `..` and decode `%2e` before the check): an optional
 * http(s)://host, then exactly `/registration-details/{uid}/billing?registration_id={uid}`
 * with alphanumeric uids (UidService). Only that route is returned and the host is
 * discarded, so a traversal, an encoded character, another scheme, a protocol-relative
 * `//host`, a hash or an extra param all return null and the caller falls back.
 */
const REGISTRATION_BILLING_LINK =
  /^(?:https?:\/\/[^/?#\s]+)?(\/registration-details\/[A-Za-z0-9]+\/billing\?registration_id=[A-Za-z0-9]+)$/

export const toRegistrationBillingRoute = (raw: unknown): string | null => {
  if (typeof raw !== 'string') {
    return null
  }

  return REGISTRATION_BILLING_LINK.exec(raw)?.[1] ?? null
}

/**
 * Never returns '' — an actionable money notice must always go somewhere. A notice with
 * no payable bill (offline, settled, charge in flight) and an OLD row that predates
 * `action_url` both land on the registrations list, which is a true statement either way.
 */
export const resolveBalanceNotificationUrl = (actionUrl: unknown): string =>
  toRegistrationBillingRoute(actionUrl) ??
  toPayRoute(actionUrl) ??
  BALANCE_NOTIFICATION_FALLBACK
